import { jsx as _jsx } from "react/jsx-runtime";
import { Button, FilterDropdown, FilterMenuItem, SearchBox } from '..';
import { useEffect, useState } from 'react';
import { classNames, isDefined } from '../../utils';
import styles from './GenericSiteFilter.module.css';
const closeFunctionInCustomDropdown = () => {
    // workaround: close the outer dropdown by simulating a mousedown event outside it
    const mouseEvent = document.createEvent('MouseEvents');
    mouseEvent.initEvent('mousedown', true, true);
    document.body.dispatchEvent(mouseEvent);
};
const GenericSiteFilter = ({ sites, selectedSiteFilters, getSites, setSiteFilter, className, dropdownClassName, isInDropdown, alignItemsRight, resetFilters, }) => {
    // Hooks
    const [query, setQuery] = useState('');
    const [selectedSiteIds, setSelectedSiteIds] = useState(new Set());
    // This external state is used to fix an issue where FilterDropdown and
    // Searchbox's internal open state would get out of sync. Due to structure of
    // components this seems the best way to solve
    const [externalOpen, setExternalOpen] = useState(false);
    useEffect(() => {
        !isInDropdown && resetFilters && setSiteFilter([]);
        if (!isInDropdown || !sites.length) {
            getSites();
        }
        if (isInDropdown) {
            setSelectedSiteIds(new Set(selectedSiteFilters));
        }
        return () => {
            setSelectedSiteIds(new Set());
            !isInDropdown && resetFilters && setSiteFilter([]);
        };
        // eslint-disable-next-line
    }, []);
    useEffect(() => {
        setSelectedSiteIds(new Set(selectedSiteFilters));
    }, [selectedSiteFilters]);
    const handleSearch = (value) => {
        setQuery(value);
    };
    const handleExternalOpen = (value) => {
        setExternalOpen(value);
        if (isInDropdown && !value) {
            closeFunctionInCustomDropdown();
        }
    };
    const handleSelectSite = (siteId, submitSelectedSites = false) => {
        const updatedSelectedSiteIds = new Set(selectedSiteIds);
        if (updatedSelectedSiteIds.has(siteId)) {
            updatedSelectedSiteIds.delete(siteId);
        }
        else {
            updatedSelectedSiteIds.add(siteId);
        }
        setSelectedSiteIds(updatedSelectedSiteIds);
        if (submitSelectedSites) {
            setSiteFilter([...updatedSelectedSiteIds]);
        }
    };
    const toggleSelectAll = () => {
        if (selectedSiteIds.size !== 0) {
            setSelectedSiteIds(new Set());
        }
        else {
            const updatedSelectedSiteIds = new Set(selectedSiteIds);
            sites.forEach((site) => updatedSelectedSiteIds.add(site.id));
            setSelectedSiteIds(updatedSelectedSiteIds);
        }
    };
    const Actions = ({ closeFunction }) => {
        return (_jsx(Button, Object.assign({ size: "small", onClick: () => {
                setSiteFilter([...selectedSiteIds]);
                if (isDefined(closeFunction)) {
                    closeFunction();
                }
                if (isInDropdown) {
                    closeFunctionInCustomDropdown();
                }
            } }, { children: "Apply" }), void 0));
    };
    const lowerCaseFilterValue = query.toLowerCase();
    const renderItems = sites
        .filter((site) => filterSites(lowerCaseFilterValue, site))
        .map((site) => (_jsx(FilterMenuItem, Object.assign({ id: site.id, title: site.title, description: site.address, onClick: () => handleSelectSite(site.id), checked: selectedSiteIds.has(site.id), highlight: query, isCompact: isInDropdown }, { children: site.title }), site.id)));
    const dropdownContent = (_jsx(SearchBox, { placeholder: "Sites, City, Zip...", query: query, onChange: handleSearch, onSelectAll: toggleSelectAll, items: renderItems, width: isInDropdown ? '100%' : '29.17rem', widthActive: isInDropdown ? '100%' : '29.17rem', withDropdown: true, isInDropdown: isInDropdown, areAnySelected: selectedSiteIds.size !== 0, actions: _jsx(Actions, {}, void 0), startOpen: true, hasCloseButton: true, error: renderItems.length === 0, externalControlOpen: isInDropdown || externalOpen, externalControlSetOpen: handleExternalOpen }, void 0));
    if (isInDropdown) {
        return dropdownContent;
    }
    return (_jsx(FilterDropdown, Object.assign({ className: className, dropdownClassName: classNames(styles.filterDropdown, dropdownClassName), title: null, filterLabels: {
            selectedItems: selectedSiteFilters,
            title: 'Site',
            transformItemName: (siteId) => {
                var _a;
                return (((_a = sites.find((site) => {
                    return +site.id === +siteId;
                })) === null || _a === void 0 ? void 0 : _a.title) || '');
            },
            removeFilter: (siteId) => handleSelectSite(siteId, true),
        }, active: false, externalControlOpen: externalOpen, externalControlSetOpen: handleExternalOpen, alignItemsRight: alignItemsRight }, { children: dropdownContent }), void 0));
};
const filterSites = (textFilter = '', site) => {
    // The api returns nulls which means the above line will not cast default values
    // The below however will override null or undefined as strings.
    const { title = '', externalId = '', address = '' } = site;
    const lowerCaseTextFilter = textFilter.toLowerCase();
    const lowerCaseTitle = (title || '').toLowerCase();
    const lowerCaseExternalId = (externalId || '').toLowerCase();
    const lowerCaseAddress = (address || '').toLowerCase();
    return (lowerCaseTitle.indexOf(lowerCaseTextFilter) > -1 ||
        lowerCaseAddress.indexOf(lowerCaseTextFilter) > -1 ||
        lowerCaseExternalId.indexOf(lowerCaseTextFilter) > -1);
};
export default GenericSiteFilter;
